/**
 * Funnel /r/cumple → Slack (#delfuocampo vía SLACK_WEBHOOK_URL) + PostHog.
 * Best-effort: nunca rompe UX.
 *
 * IMPORTANTE: `cumple_payment_return_success` = el browser volvió de MP/Stripe con
 * status OK. NO es venta acreditada. La venta REAL llega por webhook
 * (`sale_confirmed` / `sale_confirmed_server` en server) cuando MP/Stripe confirma el pago.
 */
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";
import {
  getPostHogSlackSessionPropertiesPostHog,
  getSlackFunnelDiagnosticsExtraPostHog,
} from "@/lib/posthogAnalytics";

export type CumpleFunnelKind =
  | "cta_click_birthday"
  | "page_enter_cumple"
  | "cumple_amount_selected"
  | "cumple_checkout_started"
  | "cumple_payment_return_success"
  | "cumple_payment_return_error"
  | "cumple_abandonment";

const TEMPLATE = "CumpleTemplate";

export function getCumpleSessionId(): string {
  const ph = getPostHogSlackSessionPropertiesPostHog();
  return ph.ph_session_id || `cumple_${Date.now()}`;
}

/** En `npm run dev` sin `VITE_DEV_API_PROXY`, Vite no sirve `/api/*` → 404 en consola. */
function canPostFunnelApi(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.DEV) return true;
  const proxy = (import.meta.env.VITE_DEV_API_PROXY as string | undefined)?.trim();
  return Boolean(proxy);
}

export function postCumpleSlackFunnel(
  kind: CumpleFunnelKind,
  opts?: {
    sessionId?: string;
    lastStep?: string;
    amount?: number;
    currency?: "ARS" | "USD";
    country?: string;
    checkoutGateway?: "mercadopago" | "stripe";
    errorMessage?: string;
    timeOnPageSeconds?: number;
    userEmail?: string;
    extra?: Record<string, string | number | boolean>;
    replayNearNow?: boolean;
  },
): void {
  if (typeof window === "undefined" || !canPostFunnelApi()) return;

  const replayNearNow =
    opts?.replayNearNow ??
    (kind === "cta_click_birthday" ||
      kind === "cumple_checkout_started" ||
      kind === "cumple_payment_return_success" ||
      kind === "cumple_payment_return_error" ||
      kind === "cumple_abandonment");

  const phCtx = getPostHogSlackSessionPropertiesPostHog(
    replayNearNow ? { replayNearNow: true } : undefined,
  );
  const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog(
    replayNearNow ? { replayNearNow: true } : undefined,
  );
  const ts = detectTrafficSource();

  const amount = opts?.amount;
  const currency = opts?.currency ?? "ARS";
  const country =
    opts?.country ??
    (typeof window !== "undefined"
      ? String(
          (window as Window & { __TEMPLATE_CONFIG__?: { country?: string } }).__TEMPLATE_CONFIG__
            ?.country ?? "AR",
        ).toUpperCase()
      : "AR");
  const gateway = opts?.checkoutGateway ?? (country === "AR" ? "mercadopago" : "stripe");

  const mergedExtra: Record<string, string | number | boolean> = {
    funnel: "cumple",
    page: "cumple",
    product_type: "birthday_gift",
    embedded_social_browser: isInstagramBrowser(),
    channel: ts.channel,
    source: ts.source,
    medium: ts.medium,
    ...(ts.inAppBrowser || isInstagramBrowser() ? { in_app_browser: true } : {}),
    ...(ts.raw.referrer_domain ? { referrer: ts.raw.referrer_domain } : {}),
    ...(amount != null && amount > 0
      ? currency === "USD"
        ? {
            amount_usd: amount,
            amount_display: `$${amount} USD`,
          }
        : {
            amount_ars: amount,
            amount_display: `$${amount.toLocaleString("es-AR")} ARS`,
          }
      : {}),
    ...phCtx,
    ...funnelDiag,
  };
  if (opts?.extra) {
    for (const [k, v] of Object.entries(opts.extra)) {
      if (v !== undefined && v !== null) mergedExtra[k] = v;
    }
  }

  const payload: Record<string, unknown> = {
    kind,
    template: TEMPLATE,
    country,
    flow_trace_id: opts?.sessionId || phCtx.ph_session_id || undefined,
    last_step: opts?.lastStep,
    page_url: window.location.href,
    time_on_page_seconds: opts?.timeOnPageSeconds,
    user_email: opts?.userEmail,
    error_message: opts?.errorMessage,
    amount_cents:
      amount != null && amount > 0
        ? currency === "USD"
          ? Math.round(amount * 100)
          : Math.round(amount)
        : undefined,
    currency: amount != null && amount > 0 ? currency.toLowerCase() : undefined,
    checkout_gateway: gateway,
    extra: mergedExtra,
  };

  try {
    void fetch("/api/track-funnel-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export type CumpleDraftCaptureOpts = {
  sessionId?: string;
  donorEmail: string;
  donorName?: string;
  message?: string;
  amount?: number;
  selectedAmount?: string;
  customAmount?: string;
  currency?: "ARS" | "USD";
  country?: string;
  donationId?: string | null;
  lastStep?: string;
  wizardStep?: string;
  checkoutStarted?: boolean;
};

function isCaptureEmailValid(email: string): boolean {
  const e = email.trim();
  return e.length >= 5 && e.includes("@") && e.includes(".");
}

/**
 * Guarda draft en servidor → PostgreSQL `cumple_abandonment_intents`
 * (api/lib/recoveryStorage.ts). POST /api/capture-cumple-abandonment.
 * Solo requiere email válido; monto y mensaje son opcionales (recovery cron).
 */
export function postCumpleAbandonmentCapture(opts: CumpleDraftCaptureOpts): void {
  if (typeof window === "undefined" || !canPostFunnelApi()) return;
  const email = opts.donorEmail.trim();
  if (!isCaptureEmailValid(email)) return;

  const phCtx = getPostHogSlackSessionPropertiesPostHog();

  const payload = {
    session_id: opts.sessionId || phCtx.ph_session_id,
    ph_session_id: phCtx.ph_session_id,
    ph_distinct_id: phCtx.ph_distinct_id,
    donor_email: email,
    donor_name: opts.donorName?.trim() || undefined,
    message: opts.message?.trim() || undefined,
    amount: opts.amount,
    selected_amount: opts.selectedAmount?.trim() || undefined,
    custom_amount: opts.customAmount?.trim() || undefined,
    currency: opts.currency,
    country: opts.country?.trim().toUpperCase().slice(0, 2) || undefined,
    donation_id: opts.donationId || undefined,
    last_step: opts.lastStep,
    wizard_step: opts.wizardStep || opts.lastStep,
    checkout_started: opts.checkoutStarted === true,
  };

  try {
    void fetch("/api/capture-cumple-abandonment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}